import type {
  DeliveryReliability,
  PayerReliability,
} from "@creators/backend/lib/collabs/reliability";

export type { DeliveryReliability, PayerReliability };

/** Tones map to the same palette the rest of the app uses for money and warnings. */
export type ReliabilityTone = "green" | "amber" | "red" | "neutral";

export type ReliabilitySummary = {
  /** "92%", or null when there is not enough history to put a number on it. */
  value: string | null;
  /** What the number is of — always shown, so the rate can be judged. */
  detail: string;
  tone: ReliabilityTone;
};

function percent(rate: number): string {
  return `${Math.round(rate * 100)}%`;
}

/** Compact list score — 100% → "10", 35% → "3.5". */
function scoreOutOfTen(rate: number): string {
  const score = Math.round(rate * 100) / 10;
  return Number.isInteger(score) ? String(score) : score.toFixed(1);
}

/**
 * A rate is only as good as the number of tries behind it, so the copy leads
 * with the counts and lets the percentage be the summary rather than the claim.
 * Below the sample floor the rate is null and the counts carry the whole answer.
 */
function tone(rate: number | null): ReliabilityTone {
  if (rate === null) return "neutral";
  if (rate >= 0.9) return "green";
  if (rate >= 0.7) return "amber";
  return "red";
}

/** "Will this person actually pay me?", for a creator weighing up an offer. */
/** Settled + overdue — the only tranches that count toward the pay score. */
function payerScored(payer: PayerReliability): number {
  return payer.settled + payer.overdue;
}

export function summarisePayer(payer: PayerReliability): ReliabilitySummary {
  if (payer.owed === 0) {
    return {
      value: null,
      detail: "No payment has fallen due to them yet.",
      tone: "neutral",
    };
  }

  const scored = payerScored(payer);
  const settled = `Settled ${payer.settled} of ${scored || payer.owed} ${
    (scored || payer.owed) === 1 ? "tranche" : "tranches"
  }`;

  if (payer.rate === null && scored < 3) {
    return {
      value: null,
      detail:
        scored === 0
          ? payer.awaiting > 0
            ? `${payer.awaiting} awaiting confirmation — still on time.`
            : "Open payments are still within time."
          : `${settled}. Too few to read much into.`,
      tone: "neutral",
    };
  }

  const rate = scored > 0 ? payer.settled / scored : 1;

  // An overdue tranche is the sharpest signal here, so it outranks the average.
  if (payer.overdue > 0) {
    return {
      value: rate > 0 ? percent(rate) : null,
      detail: `${settled}. ${payer.overdue} overdue right now.`,
      tone: "red",
    };
  }

  if (rate <= 0) {
    return {
      value: null,
      detail: payer.awaiting > 0 ? `${settled}. ${payer.awaiting} awaiting confirmation.` : settled,
      tone: "red",
    };
  }

  return {
    value: percent(rate),
    detail: payer.awaiting > 0 ? `${settled}. ${payer.awaiting} awaiting confirmation.` : settled,
    tone: tone(rate),
  };
}

/** "Will this person actually deliver on time?", for whoever is booking. */
export function summariseDelivery(delivery: DeliveryReliability): ReliabilitySummary {
  if (delivery.completedCollabs === 0) {
    return { value: null, detail: "No completed collabs yet.", tone: "neutral" };
  }

  const done = `On time on ${delivery.onTime} of ${delivery.completedCollabs}`;

  if (delivery.rate === null) {
    return { value: null, detail: `${done}. Too few to read much into.`, tone: "neutral" };
  }

  // Hide a literal zero — show the count detail instead of "0%" / "0".
  if (delivery.rate <= 0) {
    return { value: null, detail: done, tone: "red" };
  }

  return { value: percent(delivery.rate), detail: done, tone: tone(delivery.rate) };
}

/**
 * Compact list score for the other side (out of 10). Unlike summarise*, this
 * shows a figure as soon as there is any history — an inbox full of empty slots
 * is worse than a provisional number the detail line can qualify.
 */
export function listCounterpartTrack(input: {
  yourRole: "creator" | "brand";
  payer: PayerReliability | null;
  delivery: DeliveryReliability | null;
}): (ReliabilitySummary & { label: string }) | null {
  if (input.yourRole === "creator" && input.payer) {
    if (input.payer.owed === 0) return null;
    const scored = payerScored(input.payer);
    // Nothing scored yet (only in-time open dues) — no badge until there is
    // a settled or overdue tranche to judge.
    if (scored === 0) return null;
    const rate = input.payer.settled / scored;
    if (rate <= 0) return null;
    const summary = summarisePayer({ ...input.payer, rate });
    return {
      value: scoreOutOfTen(rate),
      detail: summary.detail,
      tone: summary.tone,
      label: "Pays",
    };
  }

  if (input.yourRole === "brand" && input.delivery) {
    if (input.delivery.completedCollabs === 0) return null;
    const rate = input.delivery.onTime / input.delivery.completedCollabs;
    if (rate <= 0) return null;
    const summary = summariseDelivery({ ...input.delivery, rate });
    return {
      value: scoreOutOfTen(rate),
      detail: summary.detail,
      tone: summary.tone,
      label: "On time",
    };
  }

  return null;
}
